import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Committee, LobbyistDoc, RegistryLobbyist } from '../types/types';
import Layout from '../components/Layout';
import { knessetFromParam, previousKnesset, swornInLabel, useScope } from '../hooks/useScope';
import KnessetMenu from '../components/KnessetMenu';
import { appearancesLabel } from '../utils/format';
import { useBreakpoint } from '../useBreakpoint';
import { committeeLiveState, useOnAir } from '../components/live/useOnAir';
import LiveBadge, { LiveState } from '../components/live/LiveBadge';
import { getCommitteeList, getLobbyistRegistry, getLobbyistsOf, getMemberSummaries, getScope, LobbyistRegistryIndex, UNTAGGED_KNESSET } from '../data/cache';
import { loadFavoriteCommittees, saveFavoriteCommittees } from '../utils/favorites';
import { buttonReset, color, font, radius, shadow, space, TAP, Tone, weight } from '../theme';
import PageHeader from '../components/PageHeader';
import TabBar from '../components/TabBar';
import SearchField from '../components/SearchField';
import Chip, { ChipRow } from '../components/Chip';
import SelectMenu, { ResultBar, SelectOption } from '../components/SelectMenu';
import { Badge } from '../components/Badge';
import { Card } from '../components/Card';
import ListRow from '../components/ListRow';
import Bar from '../components/Bar';
import Button from '../components/Button';
import Icon from '../components/Icon';
import { EmptyState, ErrorState } from '../components/States';
import Notice from '../components/Notice';
import { Skeleton, SkeletonList, SkeletonText } from '../components/Skeleton';
import PageContainer from '../layout/PageContainer';
import TwoPane from '../layout/TwoPane';
import Grid from '../layout/Grid';
import FilterSection from '../layout/FilterSection';
import { RegistryRowBadge } from '../components/lobbyists/RegistryBits';
import { clientsLabel, firmLabel, registryOnlyLobbyists, registryOnlyPath } from '../components/lobbyists/registry';

/* ── Config ─────────────────────────────────────────────────────────────── */

const typeConfig: Record<string, { label: string; tone: Tone }> = {
  standing: { label: 'קבועה',  tone: 'info' },
  special:  { label: 'מיוחדת', tone: 'neutral' },
  joint:    { label: 'משותפת', tone: 'neutral' },
};

type ActiveTab = 'committees' | 'lobbyists';
type FilterType = 'all' | 'standing' | 'special' | 'joint' | 'favorites';
type SortType = 'default' | 'lobby';
type LobbySort = 'appearances' | 'name' | 'committees' | 'clients';
// The lobbyist register filter: everyone, the registered (seen at sessions or not), or only
// the registered who were never seen at a session
type RegFilter = 'all' | 'registered' | 'regOnly';
const REG_FILTERS: RegFilter[] = ['all', 'registered', 'regOnly'];

// Desktop filter card: the type chips, and the phrase the result line uses for each sort
const TYPE_FILTERS: { key: 'standing' | 'special' | 'joint'; label: string }[] = [
  { key: 'standing', label: 'קבועות' },
  { key: 'special', label: 'מיוחדות' },
  { key: 'joint', label: 'משותפות' },
];
const SORT_PHRASE: Record<SortType, string> = { default: 'לפי סוג ושם', lobby: 'לפי לחץ שדלנים' };
const LOBBY_SORTS: { key: LobbySort; label: string; phrase: string }[] = [
  { key: 'appearances', label: 'הופעות', phrase: 'לפי מספר ההופעות' },
  { key: 'committees', label: 'ועדות', phrase: 'לפי מספר הוועדות' },
  { key: 'clients', label: 'לקוחות', phrase: 'לפי מספר הלקוחות בפנקס' },
  { key: 'name', label: 'שם', phrase: 'לפי שם' },
];
// Without the register there are no clients to sort by
const lobbySortsFor = (hasRegistry: boolean) => (hasRegistry ? LOBBY_SORTS : LOBBY_SORTS.filter(o => o.key !== 'clients'));

// Phone: the sort menus at the end of each tab's result line
const COMMITTEE_SORT_OPTIONS: SelectOption<SortType>[] = [
  { key: 'default', label: 'סוג ושם' },
  { key: 'lobby', label: 'לחץ שדלנים' },
];
const lobbySortOptions = (hasRegistry: boolean): SelectOption<LobbySort>[] => lobbySortsFor(hasRegistry).map(o => ({ key: o.key, label: o.label }));

// Lobbyists per committee member: the bar color marks heavy pressure (status only, not clickable)
const lobbyRatio = (c: Committee) => (c.memberIds?.length ? (c.lobbyistCount || 0) / c.memberIds.length : 0);
const lobbyBarColor = (ratio: number) => (ratio >= 3 ? '#E24B4A' : ratio >= 1 ? '#C77700' : '#3f7a16');

const formatMonthRange = (l: LobbyistDoc) =>
  l.firstSeen && l.lastSeen && l.firstSeen !== l.lastSeen
    ? `${l.firstSeen.substring(0, 7)} – ${l.lastSeen.substring(0, 7)}`
    : l.firstSeen?.substring(0, 7) || '';

/* ── Committee card ─────────────────────────────────────────────────────── */

interface CommitteeCardProps {
  committee: Committee;
  chairName: string | undefined; // undefined while the chair names are loading
  isFav: boolean;
  onToggleFav: (id: string) => void;
  live: LiveState; // on air ("בשידור") or on a break ("בהפסקה"), from the live feed
  maxRatio: number;
}

const CommitteeCard = ({ committee, chairName, isFav, onToggleFav, live, maxRatio }: CommitteeCardProps) => {
  const type = typeConfig[committee.type] || typeConfig.standing;
  const memberCount = committee.memberIds?.length || 0;
  const lobbyCount = committee.lobbyistCount || 0;
  const ratio = lobbyRatio(committee);

  return (
    <div style={{ position: 'relative', background: color.surface, borderRadius: radius.xl, boxShadow: shadow.card }}>
      {/* The whole card links to the committee; the star is a separate button beside the link */}
      <Link
        to={`/committees/${committee.id}`}
        state={{ from: 'ועדות' }}
        style={{ display: 'flex', flexDirection: 'column', gap: space.sm, padding: space.lg, paddingInlineEnd: TAP + space.xs, color: color.text, textDecoration: 'none' }}
      >
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <LiveBadge state={live} />
          <Badge tone={type.tone}>{type.label}</Badge>
          {committee.isCurrent === false && <Badge>הסתיימה</Badge>}
        </span>
        <span style={{ fontSize: font.body + 1, fontWeight: weight.medium, lineHeight: 1.35 }}>{committee.name}</span>
        {committee.description && (
          <span style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {committee.description}
          </span>
        )}
        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.sm, fontSize: font.small, color: color.textSecondary }}>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {committee.currentChairId
              ? <>יו״ר: <span style={{ color: color.text }}>{chairName === undefined ? '…' : chairName || 'לא ידוע'}</span></>
              : 'יו״ר: טרם מונה'}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: space.xs, flexShrink: 0 }}>
            <Icon name="users" size={16} />{memberCount} חברים
          </span>
        </span>
        {lobbyCount > 0 && (
          <span style={{ display: 'flex', flexDirection: 'column', gap: space.xs + 2 }}>
            <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: font.caption, color: color.textSecondary }}>
              <span>לחץ שדלנים</span>
              <span style={{ fontWeight: weight.medium, color: color.text }}>{lobbyCount} שדלנים · {ratio.toFixed(1)} לח״כ</span>
            </span>
            <Bar value={ratio} max={maxRatio} color={lobbyBarColor(ratio)} height={5} />
          </span>
        )}
      </Link>
      <button
        type="button"
        onClick={() => onToggleFav(committee.id)}
        aria-pressed={isFav}
        aria-label={isFav ? `הסרת ${committee.name} מהמעקב` : `מעקב אחרי ${committee.name}`}
        style={{
          ...buttonReset,
          position: 'absolute',
          top: space.xs,
          left: space.xs,
          width: TAP,
          height: TAP,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isFav ? color.primary : color.textTertiary,
        }}
      >
        <Icon name={isFav ? 'starFilled' : 'star'} size={22} />
      </button>
    </div>
  );
};

const CommitteeCardSkeleton = () => (
  <div data-skeleton="true" style={{ background: color.surface, borderRadius: radius.xl, boxShadow: shadow.card, padding: space.lg, display: 'flex', flexDirection: 'column', gap: space.md }}>
    <Skeleton width={56} height={18} />
    <Skeleton width="70%" height={16} />
    <SkeletonText lines={2} />
  </div>
);

/* ── Lobbyist row ───────────────────────────────────────────────────────── */

// One row of the tab: a lobbyist seen at committee sessions (doc), with the register's entry
// when the sync matched one, or a lobbyist known only from the register (no doc). A
// register-only row of someone seen in another Knesset links to that Knesset's page.
interface LobbyRow {
  doc: LobbyistDoc | null;
  reg: RegistryLobbyist | null;
  elsewhere?: { k: number; appearances: number; to: string; shownK: number; shownIsCurrent: boolean };
}

const rowKey = (r: LobbyRow) => (r.doc ? r.doc.id : `reg-${r.reg ? r.reg.registryId : ''}`);
const rowName = (r: LobbyRow) => (r.doc ? r.doc.fullName : r.reg ? r.reg.fullName : '');

// The register's badge and the firm (or "independent")
const registryBadges = (reg: RegistryLobbyist): React.ReactNode[] => {
  const firm = firmLabel(reg);
  return [<RegistryRowBadge key="reg" entry={reg} />].concat(firm ? [<Badge key="firm">{firm}</Badge>] : []);
};

const LobbyistRow = ({ row, first }: { row: LobbyRow; first: boolean }) => {
  const { doc: l, reg, elsewhere } = row;
  if (!l) {
    // Register only: licensed, but never recorded at a session of the Knesset shown
    if (!reg) return null;
    const notSeen = !elsewhere ? 'לא הופיע/ה בוועדות'
      : `${elsewhere.shownIsCurrent ? 'עוד ' : ''}לא הופיע/ה בוועדות הכנסת ה-${elsewhere.shownK}`;
    return (
      <ListRow
        divider={!first}
        to={elsewhere ? elsewhere.to : registryOnlyPath(reg)}
        state={{ from: 'שדלנים' }}
        title={reg.fullName}
        subtitle={
          <span style={{ display: 'flex', flexDirection: 'column', gap: space.xs + 2, marginTop: space.xxs }}>
            <span style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs }}>{registryBadges(reg)}</span>
            <span>{reg.clients.length > 0 ? `${clientsLabel(reg.clients.length)} · ` : ''}{notSeen}</span>
            {elsewhere && <span style={{ color: color.primary }}>{appearancesLabel(elsewhere.appearances)} בכנסת ה-{elsewhere.k} ›</span>}
          </span>
        }
        trailing={<Badge style={{ flexShrink: 0 }}>רק בפנקס</Badge>}
      />
    );
  }
  const primaryTitle = l.titles?.[0];
  const primaryOrg = l.organizations?.[0];
  // A registered lobbyist shows the register's badge and firm instead of the title and organization
  const badges: React.ReactNode[] = reg ? registryBadges(reg) : [];
  if (l.isLoby99) badges.push(<Badge key="l99" tone="info">לובי 99</Badge>);
  if (l.isLoby99 && l.internalRoles?.[0]) badges.push(<Badge key="role">{l.internalRoles[0]}</Badge>);
  if (!reg && !l.isLoby99 && primaryTitle) badges.push(<Badge key="title">{primaryTitle}</Badge>);
  if (!reg && !l.isLoby99 && primaryOrg) badges.push(<Badge key="org">{primaryOrg}</Badge>);

  return (
    <ListRow
      divider={!first}
      to={`/lobbyists/${l.id}`}
      state={{ from: 'שדלנים' }}
      title={l.fullName}
      subtitle={
        <span style={{ display: 'flex', flexDirection: 'column', gap: space.xs + 2, marginTop: space.xxs }}>
          {badges.length > 0 && <span style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs }}>{badges}</span>}
          <span style={{ display: 'flex', gap: space.sm, alignItems: 'baseline' }}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {l.committees.length} ועדות
              {l.committees.length > 0 && <> · {l.committees[0].committeeName}</>}
              {l.committees.length > 1 && <> (ועוד {l.committees.length - 1})</>}
            </span>
            {formatMonthRange(l) && <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{formatMonthRange(l)}</span>}
          </span>
        </span>
      }
      trailing={<Badge tone="info" style={{ flexShrink: 0 }}>{appearancesLabel(l.totalAppearances)}</Badge>}
    />
  );
};

/* ── Page ───────────────────────────────────────────────────────────────── */

const CommitteesPage = () => {
  const { isTablet, isDesktop } = useBreakpoint();
  const [searchParams, setSearchParams] = useSearchParams();
  // Desktop: chip rows wrap inside the filter column instead of scrolling sideways
  const chipWrap: React.CSSProperties | undefined = isDesktop ? { flexWrap: 'wrap', overflowX: 'visible' } : undefined;

  const activeTab: ActiveTab = (searchParams.get('tab') as ActiveTab) || 'committees';
  const filter: FilterType = (searchParams.get('filter') as FilterType) || 'all';
  const sort: SortType = (searchParams.get('sort') as SortType) || 'default';
  const search: string = searchParams.get('q') || '';
  const lobbySearch: string = searchParams.get('lq') || '';
  const lobbyOrgType: string = searchParams.get('lobbyType') || 'all';
  const lobbySortParam: LobbySort = (searchParams.get('lobbySort') as LobbySort) || 'appearances';
  const regParam = searchParams.get('reg') as RegFilter | null;
  // The Knesset shown (?k=): an archived one, or the current one. The lobbyists tab, without
  // ?k=, shows the previous Knesset's while the current one has no lobbyist yet (decision 3B)
  const kParam = searchParams.get('k');

  const [committees, setCommittees] = useState<Committee[] | null>(null);
  const [committeesError, setCommitteesError] = useState(false);
  const [chairNames, setChairNames] = useState<Record<string, string> | null>(null);
  const [lobbyists, setLobbyists] = useState<LobbyistDoc[] | null>(null);
  const [lobbyistsError, setLobbyistsError] = useState(false);
  // The Knesset of `lobbyists`, whether it was picked for lack of the current one's, and another
  // Knesset's lobbyists (by doc ID) for the register-only rows of people seen there
  const [lobbyView, setLobbyView] = useState<{ k: number; defaulted: boolean; otherK: number | null; other: Record<string, LobbyistDoc> } | null>(null);
  // The lobbyist register, set together with `lobbyists`; null when it can't be read
  const [registry, setRegistry] = useState<LobbyistRegistryIndex | null>(null);
  // Without the register, its filter and sort fall back to the plain list
  const lobbyReg: RegFilter = registry && regParam && REG_FILTERS.indexOf(regParam) >= 0 ? regParam : 'all';
  const lobbySort: LobbySort = lobbySortParam === 'clients' && !registry ? 'appearances' : lobbySortParam;
  const [reloadKey, setReloadKey] = useState(0);
  const scope = useScope();
  const [favorites, setFavorites] = useState<Set<string>>(loadFavoriteCommittees);
  // Live badges: the live feed merged with meta/scheduleNow (re-read every minute; non-blocking)
  const onAir = useOnAir();

  const updateParam = useCallback((key: string, value: string, defaultValue: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === defaultValue) next.delete(key);
      else next.set(key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setActiveTab = (v: ActiveTab) => updateParam('tab', v, 'committees');
  const setFilter = (v: FilterType) => updateParam('filter', v, 'all');
  const setSort = (v: SortType) => updateParam('sort', v, 'default');
  const setSearch = (v: string) => updateParam('q', v, '');
  const setLobbySearch = (v: string) => updateParam('lq', v, '');
  const setLobbyOrgType = (v: string) => updateParam('lobbyType', v, 'all');
  const setLobbySort = (v: LobbySort) => updateParam('lobbySort', v, 'appearances');
  // The register filter and the organization type in one URL update
  const setLobbyFilters = (reg: RegFilter, orgType: string) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (reg === 'all') next.delete('reg'); else next.set('reg', reg);
    if (orgType === 'all') next.delete('lobbyType'); else next.set('lobbyType', orgType);
    return next;
  }, { replace: true });

  // Committees render as soon as they arrive; chair names fill in afterwards
  useEffect(() => {
    let cancelled = false;
    setCommitteesError(false);
    setCommittees(null);
    Promise.all([getCommitteeList(), getScope()]).then(([all, sc]) => {
      if (cancelled) return;
      // One Knesset's committees: the current one's, or an archived one's (?k=). An earlier
      // Knesset's committees keep their docs and open from their own pages too.
      const k = knessetFromParam(kParam, sc);
      const list = all.filter(c => (c.knessetNumber ?? UNTAGGED_KNESSET) === k);
      const typeOrder: Record<string, number> = { standing: 0, special: 1, joint: 2 };
      // Active committees first (ended ones last), then by type, then by name
      const sorted = list.slice().sort((a, b) => {
        const ae = a.isCurrent === false ? 1 : 0; const be = b.isCurrent === false ? 1 : 0;
        if (ae !== be) return ae - be;
        const ao = typeOrder[a.type] ?? 3; const bo = typeOrder[b.type] ?? 3;
        if (ao !== bo) return ao - bo;
        return a.name.localeCompare(b.name, 'he');
      });
      setCommittees(sorted);
      return getMemberSummaries(sorted.map(c => c.currentChairId)).then(members => {
        if (cancelled) return;
        const names: Record<string, string> = {};
        for (const c of sorted) if (c.currentChairId) names[c.id] = members[c.currentChairId]?.name || '';
        setChairNames(names);
      }).catch(() => { if (!cancelled) setChairNames({}); });
    }).catch(() => { if (!cancelled) setCommitteesError(true); });
    return () => { cancelled = true; };
  }, [reloadKey, kParam]);

  useEffect(() => {
    let cancelled = false;
    setLobbyistsError(false);
    setLobbyists(null);
    // The register is optional: without it the tab lists the lobbyists seen at sessions, as before
    Promise.all([getScope(), getLobbyistsOf(), getLobbyistRegistry().catch(() => null)])
      .then(async ([sc, current, reg]) => {
        const prev = previousKnesset(sc);
        const k = kParam !== null ? knessetFromParam(kParam, sc) : current.length === 0 && prev !== null ? prev : sc.knesset;
        const list = k === sc.knesset ? current : await getLobbyistsOf(k);
        // The other Knesset: the previous one beside the current one, the current one beside an archived one
        const otherK = k === sc.knesset ? prev : sc.knesset;
        const otherList = otherK === null ? [] : otherK === sc.knesset ? current : await getLobbyistsOf(otherK).catch(() => []);
        const other: Record<string, LobbyistDoc> = {};
        for (const l of otherList) other[l.id] = l;
        if (cancelled) return;
        setRegistry(reg);
        setLobbyView({ k, defaulted: kParam === null && k !== sc.knesset, otherK, other });
        setLobbyists(list);
      })
      .catch(() => { if (!cancelled) setLobbyistsError(true); });
    return () => { cancelled = true; };
  }, [reloadKey, kParam]);

  // Restore scroll position after data loads
  const committeesReady = committees !== null;
  const lobbyistsReady = lobbyists !== null;
  useEffect(() => {
    const ready = activeTab === 'committees' ? committeesReady : lobbyistsReady;
    if (!ready) return;
    try {
      const saved = sessionStorage.getItem(`committees_scroll_${activeTab}`);
      if (saved) {
        window.scrollTo(0, parseInt(saved, 10) || 0);
        sessionStorage.removeItem(`committees_scroll_${activeTab}`);
      }
    } catch { /* ok */ }
  }, [committeesReady, lobbyistsReady, activeTab]);

  // Save scroll position on unmount
  useEffect(() => {
    return () => {
      try { sessionStorage.setItem(`committees_scroll_${activeTab}`, String(window.scrollY)); } catch { /* ok */ }
    };
  }, [activeTab]);

  const toggleFavorite = useCallback((id: string) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      saveFavoriteCommittees(next);
      return next;
    });
  }, []);

  // ── Committees filtering/sorting ──
  const filtered = useMemo(() => {
    let result = committees || [];
    if (filter === 'favorites') result = result.filter(c => favorites.has(c.id));
    else if (filter !== 'all') result = result.filter(c => c.type === filter);
    if (search.trim()) {
      const q = search.trim();
      result = result.filter(c => c.name.includes(q) || (chairNames?.[c.id] || '').includes(q));
    }
    if (sort === 'lobby') result = result.slice().sort((a, b) => lobbyRatio(b) - lobbyRatio(a));
    return result;
  }, [committees, filter, sort, search, favorites, chairNames]);

  const maxRatio = useMemo(() => {
    let mx = 0.001;
    for (const c of filtered) { const r = lobbyRatio(c); if (r > mx) mx = r; }
    return mx;
  }, [filtered]);

  const typeCounts = useMemo(() => {
    const s = { standing: 0, special: 0, joint: 0 };
    for (const c of committees || []) {
      if (c.type === 'standing') s.standing++;
      else if (c.type === 'special') s.special++;
      else if (c.type === 'joint') s.joint++;
    }
    return s;
  }, [committees]);

  // ── Lobbyists filtering/sorting ──
  const allOrgTypes = useMemo(() => {
    const types = new Set<string>();
    for (const l of lobbyists || []) for (const t of (l.organizationTypes || [])) types.add(t);
    return Array.from(types).sort();
  }, [lobbyists]);

  // Desktop: lobbyists per organization type, for the chip counts
  const orgTypeCounts = useMemo(() => {
    const counts: Record<string, number> = { 'לובי 99': 0 };
    for (const l of lobbyists || []) {
      if (l.isLoby99) counts['לובי 99']++;
      for (const t of (l.organizationTypes || [])) if (t !== 'לובי 99') counts[t] = (counts[t] || 0) + 1;
    }
    return counts;
  }, [lobbyists]);

  // Every row of the tab: the lobbyists seen at sessions (with their register entry when
  // matched), then the register's lobbyists never seen at one, by name
  const lobbyRows = useMemo((): LobbyRow[] => {
    if (!lobbyists) return [];
    const rows: LobbyRow[] = lobbyists.map(l => ({
      doc: l,
      reg: registry && Object.prototype.hasOwnProperty.call(registry.byDocId, l.id) ? registry.byDocId[l.id] : null,
    }));
    if (registry) {
      const known: Record<string, true> = {};
      for (const l of lobbyists) known[l.id] = true;
      for (const r of registryOnlyLobbyists(registry, known)) {
        // Seen at the other Knesset's sessions: that Knesset's page, and how often
        const seen = lobbyView && lobbyView.otherK !== null && r.docId && Object.prototype.hasOwnProperty.call(lobbyView.other, r.docId) ? lobbyView.other[r.docId] : null;
        const elsewhere = seen && lobbyView && lobbyView.otherK !== null ? {
          k: lobbyView.otherK,
          appearances: seen.totalAppearances,
          to: `/lobbyists/${encodeURIComponent(seen.id)}${lobbyView.otherK !== scope.knesset ? `?k=${lobbyView.otherK}` : ''}`,
          shownK: lobbyView.k,
          shownIsCurrent: lobbyView.k === scope.knesset,
        } : undefined;
        rows.push({ doc: null, reg: r, elsewhere });
      }
    }
    return rows;
  }, [lobbyists, registry, lobbyView, scope.knesset]);

  const regCounts = useMemo(() => {
    let registered = 0; let regOnly = 0;
    for (const r of lobbyRows) { if (r.reg) registered++; if (!r.doc) regOnly++; }
    return { registered, regOnly };
  }, [lobbyRows]);

  const filteredLobbyists = useMemo(() => {
    let result = lobbyRows;
    if (lobbyReg === 'registered') result = result.filter(r => r.reg);
    else if (lobbyReg === 'regOnly') result = result.filter(r => !r.doc);
    if (lobbySearch.trim()) {
      const q = lobbySearch.trim().toLowerCase();
      const has = (s: string | null | undefined) => !!s && s.toLowerCase().includes(q);
      // Name, organizations and titles; the register's name, firm and clients
      result = result.filter(({ doc: l, reg }) =>
        (!!l && (has(l.fullName) || (l.organizations || []).some(o => has(o)) || (l.titles || []).some(t => has(t)))) ||
        (!!reg && (has(reg.fullName) || has(reg.firm) || reg.clients.some(c => has(c.name))))
      );
    }
    // Organization types come from the sessions, so register-only rows have none
    if (lobbyOrgType === 'לובי 99') result = result.filter(r => !!r.doc && r.doc.isLoby99);
    else if (lobbyOrgType !== 'all') result = result.filter(r => !!r.doc && (r.doc.organizationTypes || []).includes(lobbyOrgType));
    // Register-only rows have no appearances or committees, so they end those sorts
    return result.slice().sort((a, b) => {
      if (lobbySort === 'appearances') return (b.doc ? b.doc.totalAppearances : 0) - (a.doc ? a.doc.totalAppearances : 0);
      if (lobbySort === 'committees') return (b.doc ? b.doc.committees.length : 0) - (a.doc ? a.doc.committees.length : 0);
      if (lobbySort === 'clients') return (b.reg ? b.reg.clients.length : 0) - (a.reg ? a.reg.clients.length : 0);
      return rowName(a).localeCompare(rowName(b), 'he');
    });
  }, [lobbyRows, lobbyReg, lobbySearch, lobbyOrgType, lobbySort]);

  const retry = () => setReloadKey(k => k + 1);

  // Clears one tab's search and filters in a single URL update, keeping the tab and the sort
  const clearParams = (keys: string[]) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    for (const k of keys) next.delete(k);
    return next;
  }, { replace: true });

  // Desktop, above the results (as on Legislation): the count and sort, or the filtered count with a reset
  const resultLine = (text: string, filtered: boolean, onClear: () => void) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: font.small, color: color.textSecondary, marginBottom: space.sm }}>
      <span>{text}</span>
      {filtered && <button type="button" onClick={onClear} style={{ ...buttonReset, color: color.primary, fontWeight: weight.medium, minHeight: 32 }}>ניקוי הסינון</button>}
    </div>
  );
  const committeesFiltered = filter !== 'all' || search.trim() !== '';
  const lobbyistsFiltered = lobbyOrgType !== 'all' || lobbySearch.trim() !== '' || lobbyReg !== 'all';
  const lobbySorts = lobbySortsFor(!!registry);
  const lobbyFilterKeys = ['lq', 'lobbyType', 'reg'];
  const lobbySearchPlaceholder = registry ? 'חיפוש לפי שם, ארגון, תפקיד או לקוח' : 'חיפוש לפי שם, ארגון או תפקיד';
  // Desktop order: 'לובי 99' first and 'אחר' last, the rest alphabetical
  const desktopOrgTypes = ['לובי 99']
    .concat(allOrgTypes.filter(t => t !== 'לובי 99' && t !== 'אחר'))
    .concat(allOrgTypes.indexOf('אחר') >= 0 ? ['אחר'] : []);
  const viewKnesset = knessetFromParam(kParam, scope);
  const previous = previousKnesset(scope);
  // A new Knesset whose committees aren't formed yet
  const earlyEmpty = !!committees && committees.length === 0 && viewKnesset === scope.knesset && previous !== null;
  // An explicit choice on the lobbyists tab stays in the URL even for the current Knesset, so
  // choosing it while it has no lobbyists shows it (empty) rather than the default
  const setKnesset = (k: number) => setSearchParams(prev => {
    const next = new URLSearchParams();
    const tab = prev.get('tab');
    if (tab) next.set('tab', tab);
    if (k !== scope.knesset || tab === 'lobbyists') next.set('k', String(k));
    return next;
  }, { replace: true });
  const lobbyKnesset = lobbyView ? lobbyView.k : viewKnesset;
  // The current Knesset shown with no lobbyist yet, and an earlier one to offer instead
  const lobbyEarly = !!lobbyists && lobbyists.length === 0 && lobbyKnesset === scope.knesset && previous !== null;
  // The subtitle follows the tab: the committees and their lobbyists when both are the same Knesset's
  const subtitle = activeTab === 'lobbyists'
    ? (lobbyists && lobbyists.length > 0
      ? `שדלני הכנסת ה-${lobbyKnesset} · ${lobbyists.length} הופיעו בוועדות${registry ? ` · ${regCounts.registered} רשומים בפנקס` : ''}`
      : `שדלני הכנסת ה-${lobbyKnesset}`)
    : committees && lobbyists && lobbyKnesset === viewKnesset
      ? `${committees.length} ועדות · ${lobbyists.length} שדלנים שהופיעו בהן${registry ? ` · ${regCounts.registered} רשומים בפנקס` : ''}`
      : committees ? `${committees.length} ועדות` : `ועדות הכנסת ה-${viewKnesset} והשדלנים שהופיעו בהן`;

  return (
    <Layout width="full">
      <PageContainer>
      <div style={{ padding: isDesktop ? 0 : `${space.sm}px ${space.lg}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.lg }}>
        <PageHeader title="ועדות" subtitle={subtitle} action={<KnessetMenu value={activeTab === 'committees' ? viewKnesset : lobbyKnesset} onChange={setKnesset} />} />

        <TabBar
          label="תצוגה"
          tabs={[
            { key: 'committees', label: 'ועדות', count: committees?.length },
            { key: 'lobbyists', label: 'שדלנים', count: lobbyists ? (lobbyEarly ? 0 : lobbyRows.length) : undefined },
          ]}
          active={activeTab}
          onChange={setActiveTab}
          style={isDesktop ? { maxWidth: 420 } : undefined}
        />

        {/* The current Knesset has no lobbyist yet, so the tab shows the previous one's */}
        {activeTab === 'lobbyists' && lobbyView && lobbyView.defaulted && (
          <Notice style={isDesktop ? { maxWidth: 720 } : undefined}>
            בוועדות הכנסת ה-{scope.knesset} עוד לא נרשמו שדלנים: הפרוטוקולים מתפרסמים שבועות עד חודשים אחרי הישיבה. בינתיים מוצגים שדלני הכנסת ה-{lobbyView.k}.
          </Notice>
        )}

        {/* ═══ COMMITTEES TAB ═══ (desktop: search and filters in a sticky column beside the cards) */}
        {activeTab === 'committees' && (
          <TwoPane sideWidth={300} gap={space.xxl} side={isDesktop ? (
            // Desktop: search, then one card with a labelled section per kind of filter (as on Legislation)
            <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
              <SearchField value={search} onChange={setSearch} placeholder="חיפוש ועדה או יו״ר" />
              <Card>
                <FilterSection label="סוג ועדה">
                  <Chip variant="flat" block selected={filter === 'all'} onClick={() => setFilter('all')} count={committees?.length}>כל הוועדות</Chip>
                  <Grid cols={{ desktop: 3 }} gap={6}>
                    {TYPE_FILTERS.map(t => (
                      <Chip key={t.key} variant="flat" block selected={filter === t.key} onClick={() => setFilter(t.key)} count={committees ? typeCounts[t.key] : undefined}>{t.label}</Chip>
                    ))}
                  </Grid>
                </FilterSection>
                <FilterSection label="מעקב">
                  <Chip variant="flat" block selected={filter === 'favorites'} onClick={() => setFilter(filter === 'favorites' ? 'all' : 'favorites')} count={favorites.size || undefined}>
                    <Icon name={filter === 'favorites' ? 'starFilled' : 'star'} size={16} />ועדות במעקב
                  </Chip>
                </FilterSection>
                <FilterSection label="מיון" last>
                  <Grid cols={{ desktop: 2 }} gap={6}>
                    <Chip variant="flat" block selected={sort === 'default'} onClick={() => setSort('default')}>סוג ושם</Chip>
                    <Chip variant="flat" block selected={sort === 'lobby'} onClick={() => setSort('lobby')}>לחץ שדלנים</Chip>
                  </Grid>
                </FilterSection>
              </Card>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
              <SearchField value={search} onChange={setSearch} placeholder="חיפוש ועדה או יו״ר" />
              <ChipRow style={chipWrap}>
                <Chip selected={filter === 'all'} onClick={() => setFilter('all')} count={committees ? committees.length : undefined}>הכל</Chip>
                <Chip selected={filter === 'favorites'} onClick={() => setFilter('favorites')} count={favorites.size || undefined}>
                  <Icon name={filter === 'favorites' ? 'starFilled' : 'star'} size={16} />במעקב
                </Chip>
                <Chip selected={filter === 'standing'} onClick={() => setFilter('standing')} count={committees ? typeCounts.standing : undefined}>קבועות</Chip>
                <Chip selected={filter === 'special'} onClick={() => setFilter('special')} count={committees ? typeCounts.special : undefined}>מיוחדות</Chip>
                <Chip selected={filter === 'joint'} onClick={() => setFilter('joint')} count={committees ? typeCounts.joint : undefined}>משותפות</Chip>
              </ChipRow>
              <ResultBar action={<SelectMenu label="מיון" value={sort} onChange={setSort} options={COMMITTEE_SORT_OPTIONS} />}>
                {committees ? `${filtered.length} ועדות` : ''}
              </ResultBar>
            </div>
          )}>

            {committeesError && <Card padding={0}><ErrorState onRetry={retry} /></Card>}
            {isDesktop && committees && !committeesError && resultLine(
              committeesFiltered ? `${filtered.length} תוצאות מתוך ${committees.length}` : `${committees.length} ועדות, ${SORT_PHRASE[sort]}`,
              committeesFiltered,
              () => clearParams(['q', 'filter']),
            )}

            {!committeesError && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isDesktop
                  ? 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))'
                  : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
                gap: space.md,
              }}>
                {!committees && [0, 1, 2, 3].map(i => <CommitteeCardSkeleton key={i} />)}
                {filtered.map(c => (
                  <CommitteeCard
                    key={c.id}
                    committee={c}
                    chairName={chairNames ? chairNames[c.id] : undefined}
                    isFav={favorites.has(c.id)}
                    onToggleFav={toggleFavorite}
                    live={committeeLiveState(onAir, c.id)}
                    maxRatio={maxRatio}
                  />
                ))}
              </div>
            )}

            {earlyEmpty && previous !== null && (
              <Card padding={0}>
                <EmptyState
                  icon="users"
                  title="הוועדות עוד לא הוקמו"
                  body={`הכנסת ה-${scope.knesset} הושבעה${swornInLabel(scope) ? ` ב-${swornInLabel(scope)}` : ''}. הוועדות שלה יוקמו בשבועות הקרובים ויופיעו כאן.`}
                  action={<Button variant="primary" onClick={() => setKnesset(previous)}>לוועדות הכנסת ה-{previous}</Button>}
                />
              </Card>
            )}
            {committees && !earlyEmpty && filtered.length === 0 && filter === 'favorites' && !search.trim() && (
              <Card padding={0}>
                <EmptyState icon="star" title="עדיין לא סימנת ועדות למעקב" body="לחצו על הכוכב בכרטיס של ועדה כדי לראות אותה כאן." />
              </Card>
            )}
            {committees && !earlyEmpty && filtered.length === 0 && (filter !== 'favorites' || search.trim()) && (
              <Card padding={0}>
                <EmptyState
                  title="לא נמצאו ועדות"
                  body={search.trim() ? `אין ועדה או יו״ר שמתאימים ל"${search.trim()}".` : undefined}
                  action={<Button variant="ghost" onClick={() => { setSearch(''); setFilter('all'); }}>הצגת כל הוועדות</Button>}
                />
              </Card>
            )}
          </TwoPane>
        )}

        {/* ═══ LOBBYISTS TAB ═══ (desktop: search and filters in a sticky column beside the list) */}
        {activeTab === 'lobbyists' && lobbyEarly && previous !== null && (
          <Card padding={0}>
            <EmptyState
              icon="users"
              title={`עוד לא נרשמו שדלנים בכנסת ה-${scope.knesset}`}
              body={`שדלנים נרשמים בפרוטוקולים של ישיבות הוועדות, שמתפרסמים שבועות עד חודשים אחרי הישיבה. כשיופיע השדלן הראשון, הלשונית תעבור לכנסת ה-${scope.knesset} מעצמה.`}
              action={<Button variant="primary" onClick={() => setKnesset(previous)}>לשדלני הכנסת ה-{previous}</Button>}
            />
          </Card>
        )}
        {activeTab === 'lobbyists' && !lobbyEarly && (
          <TwoPane sideWidth={300} gap={space.xxl} side={isDesktop ? (
            // Desktop: search, then one card with a labelled section per kind of filter (as on Legislation)
            <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
              <SearchField value={lobbySearch} onChange={setLobbySearch} placeholder={lobbySearchPlaceholder} />
              <Card>
                {registry && (
                  <FilterSection label="פנקס השדלנים">
                    <Chip variant="flat" block selected={lobbyReg === 'all'} onClick={() => setLobbyFilters('all', lobbyOrgType)} count={lobbyRows.length}>כל השדלנים</Chip>
                    <Grid cols={{ desktop: 2 }} gap={6}>
                      {/* "רשומים", not "רשומים בפנקס": the section names the register, and a half-width chip fits no more */}
                      <Chip variant="flat" block selected={lobbyReg === 'registered'} onClick={() => setLobbyFilters('registered', lobbyOrgType)} count={regCounts.registered}>רשומים</Chip>
                      {/* Register-only lobbyists have no organization type, so this one resets it */}
                      <Chip variant="flat" block selected={lobbyReg === 'regOnly'} onClick={() => setLobbyFilters('regOnly', 'all')} count={regCounts.regOnly}>רק בפנקס</Chip>
                    </Grid>
                  </FilterSection>
                )}
                <FilterSection label="סוג ארגון">
                  <Chip variant="flat" block selected={lobbyOrgType === 'all'} onClick={() => setLobbyOrgType('all')} count={lobbyists ? lobbyRows.length : undefined}>כל הסוגים</Chip>
                  <Grid cols={{ desktop: 2 }} gap={6}>
                    {desktopOrgTypes.map((type, i) => {
                      // An odd last chip spans the row, so the grid ends evenly
                      const spans = i === desktopOrgTypes.length - 1 && desktopOrgTypes.length % 2 === 1;
                      return (
                        <div key={type} style={spans ? { gridColumn: '1 / -1' } : undefined}>
                          <Chip variant="flat" block selected={lobbyOrgType === type}
                            onClick={() => setLobbyFilters(lobbyReg === 'regOnly' ? 'all' : lobbyReg, type)}
                            count={lobbyists ? orgTypeCounts[type] || 0 : undefined}>{type}</Chip>
                        </div>
                      );
                    })}
                  </Grid>
                </FilterSection>
                <FilterSection label="מיון" last>
                  <Grid cols={{ desktop: lobbySorts.length === 4 ? 2 : 3 }} gap={6}>
                    {lobbySorts.map(o => (
                      <Chip key={o.key} variant="flat" block selected={lobbySort === o.key} onClick={() => setLobbySort(o.key)}>{o.label}</Chip>
                    ))}
                  </Grid>
                </FilterSection>
              </Card>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
              <SearchField value={lobbySearch} onChange={setLobbySearch} placeholder={lobbySearchPlaceholder} />
              {/* One choice at a time: the register chips and the organization types replace each other */}
              <ChipRow style={chipWrap}>
                <Chip selected={lobbyOrgType === 'all' && lobbyReg === 'all'} onClick={() => setLobbyFilters('all', 'all')} count={lobbyists ? lobbyRows.length : undefined}>הכל</Chip>
                {registry && <Chip selected={lobbyReg === 'registered'} onClick={() => setLobbyFilters('registered', 'all')} count={regCounts.registered}>רשומים בפנקס</Chip>}
                {/* Set from the desktop filter column; shown here only while it applies */}
                {registry && lobbyReg === 'regOnly' && <Chip selected onClick={() => setLobbyFilters('regOnly', 'all')} count={regCounts.regOnly}>רק בפנקס</Chip>}
                {(['לובי 99'].concat(allOrgTypes.filter(t => t !== 'לובי 99'))).map(type => (
                  <Chip key={type} selected={lobbyOrgType === type} onClick={() => setLobbyFilters('all', type)}
                    count={lobbyists ? orgTypeCounts[type] || 0 : undefined}>
                    {type}
                  </Chip>
                ))}
              </ChipRow>
              <ResultBar action={<SelectMenu label="מיון" value={lobbySort} onChange={setLobbySort} options={lobbySortOptions(!!registry)} />}>
                {lobbyists ? `${filteredLobbyists.length} שדלנים${lobbyReg === 'registered' ? ' רשומים בפנקס' : lobbyReg === 'regOnly' ? ' רק בפנקס' : ''}` : ''}
              </ResultBar>
            </div>
          )}>

            {lobbyistsError && <Card padding={0}><ErrorState onRetry={retry} /></Card>}
            {isDesktop && lobbyists && !lobbyistsError && resultLine(
              lobbyistsFiltered
                ? `${filteredLobbyists.length} תוצאות מתוך ${lobbyRows.length}`
                : `${lobbyRows.length} שדלנים, ${(lobbySorts.find(o => o.key === lobbySort) || lobbySorts[0]).phrase}`,
              lobbyistsFiltered,
              () => clearParams(lobbyFilterKeys),
            )}
            {!lobbyistsError && !lobbyists && <SkeletonList rows={6} />}
            {lobbyists && filteredLobbyists.length === 0 && (
              <Card padding={0}>
                <EmptyState
                  title="לא נמצאו שדלנים"
                  action={<Button variant="ghost" onClick={() => clearParams(lobbyFilterKeys)}>הצגת כל השדלנים</Button>}
                />
              </Card>
            )}
            {filteredLobbyists.length > 0 && (
              <Card padding={0}>
                {filteredLobbyists.map((r, i) => <LobbyistRow key={rowKey(r)} row={r} first={i === 0} />)}
              </Card>
            )}
          </TwoPane>
        )}
      </div>
      </PageContainer>
    </Layout>
  );
};

export default CommitteesPage;
